import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { CustomInput } from "@/components/forms/customInput"
import { Button } from "@/components/ui/button"
import {
  type ForgotPasswordFormData,
  forgotPasswordSchema,
} from "@/features/auth/schemas/auth.schema"
import { useAuthForm } from "../../hooks/useAuthForm"
import { forgotPassword } from "../../services/auth.service"

export function MobileForgotPasswordPage() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const [formState, setFormState] = useState<"default" | "success">("default")
  const [storedEmail, setStoredEmail] = useState("")

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    forgotPasswordSchema,
    async (data: ForgotPasswordFormData) => {
      await forgotPassword(data.email)
      setStoredEmail(data.email)
      setFormState("success")
    },
  )

  const handleResend = async () => {
    if (storedEmail) {
      await forgotPassword(storedEmail)
    }
  }

  const emailRegister = form.register("email")

  if (formState === "success") {
    return (
      <div className="space-y-5">
        <div className="text-center">
          <h2 className="text-2xl font-semibold leading-normal text-[#F2F0EBE6]">
            {t("forgotPassword.successTitle")}
          </h2>
        </div>

        <div className="space-y-4 text-center mb-6.5">
          <p className="text-primary text-sm leading-5.5">
            {t("forgotPassword.successDescription")}
          </p>
        </div>

        <div className="text-center text-sm leading-6.25">
          Didn't get it? Check spam or{" "}
          <button
            type="button"
            className="text-sm text-primary underline"
            onClick={handleResend}
            disabled={isSubmitting}
          >
            request again.
          </button>
        </div>

        <div className="mt-7.5">
          <Button
            type="button"
            variant="primary"
            size="md"
            className="rounded-full uppercase text-sm font-semibold py-3.75"
            fullWidth
            onClick={() => navigate("/login", { replace: true })}
          >
            {t("forgotPassword.doneButton")}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="text-center mb-9">
          <h2 className="text-2xl font-semibold leading-normal text-[#F2F0EBE6]">
            {t("forgotPassword.title")}
          </h2>
          <p className="text-primary text-sm leading-5.5  mt-5">
            {t("forgotPassword.description")}
          </p>
        </div>

        <div className="space-y-7.5">
          <CustomInput
            label={t("forgotPassword.emailLabel")}
            type="email"
            placeholder={t("forgotPassword.emailPlaceholder")}
            required={true}
            {...emailRegister}
            error={(form.formState.errors.email?.message || serverError) as string | undefined}
            disabled={isSubmitting}
            onChange={e => {
              emailRegister.onChange?.(e)
              clearServerError()
            }}
          />

          <div className="">
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="rounded-full uppercase py-3.75 h-12.5 text-sm font-semibold"
              isLoading={isSubmitting}
              fullWidth
            >
              {t("forgotPassword.submitButton")}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
